/**
 * External dependencies.
 */
import React, { useMemo, useState } from "react";
import type { AppProps } from "next/app";
import App from "next/app";
import { useRouter } from "next/router";
import { setupI18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

/**
 * Internal dependencies.
 */
import { fetchAPI, getLocale } from "../utils";
import GlobalContext from "contexts/global";
import { SearchProvider } from "contexts/search-context";
import Bugsnag from "../lib/bugsnag";
import { isDebugEnabled } from "../lib/debug";
import "../assets/scss/main.scss";
import LangProvider from "contexts/lang-context";
import Head from "next/head";
import Tracking from "components/privacy-consent/tracking";

// @ts-ignore
const ErrorBoundary = (isDebugEnabled(process.env.NEXT_PUBLIC_DEBUG) || !process.env.NEXT_PUBLIC_BUGSNAG_API_KEY)
  ? ({ children }: any) => <>{children}</>
  : // @ts-ignore
    Bugsnag.getPlugin("react").createErrorBoundary(React);




function MyApp({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const locale: any = router.locale || router.defaultLocale;
  const [queryClient] = useState(() => new QueryClient());
  // Each render tree owns its locale state; client-side locale changes get the matching catalog.
  const i18n = useMemo(() => setupI18n({
    locale,
    messages: { [locale]: pageProps.translation ?? {} },
  }), [locale, pageProps.translation]);

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1"
        />
      </Head>
      <ErrorBoundary>
        <Tracking />
        <QueryClientProvider client={queryClient}>
          <GlobalContext.Provider value={pageProps}>
            <SearchProvider>
              <LangProvider>
                <I18nProvider i18n={i18n}>
                  <Component {...pageProps} />
                </I18nProvider>
              </LangProvider>
            </SearchProvider>
          </GlobalContext.Provider>
        </QueryClientProvider>
      </ErrorBoundary>
    </>
  );
}

MyApp.getInitialProps = async (ctx: any) => {
  const appProps = await App.getInitialProps(ctx);
  const globalRes = await fetchAPI("/global", {
    populate: {
      HeaderLeftMenu: "*",
      HeaderRightMenu: "*",
      Home: {
        populate: "*",
      },
      Favico: "*",
      FooterLinks: {
        populate: "*",
      },
      SocialLinks: "*",
      BottomLinks: "*",
    },
    locale: getLocale(ctx.ctx.locale),
  });
  const strapiLocales = await fetchAPI("/i18n/locales", {
    populate: "*",
  });

  return {
    ...appProps,
    pageProps: { ...appProps.pageProps, global: globalRes?.data?.attributes ?? {}, strapiLocales },
  };
};

export default MyApp;
